import React, { useEffect } from 'react';
import { Box } from '@wix/design-system';
import { useAtom, useSetAtom } from 'jotai';
import s from './LoadingGrid.scss';
import { useLoadingSteps } from '../../hooks/useLoadingSteps';
import { useImageLoader } from '../../hooks/useImageLoader';
import { ProgressSection } from '../ProgressSection/ProgressSection';
import { FooterSection } from '../FooterSection/FooterSection';
import { activeImagesAtom, enabledImagesAtom, progressAtom } from '../../context/loadingProgress';
import { ENABLE_DELAY, TOTAL_ANIMATION_TIME } from '../../constants';
import { LoadingGridProps } from '../../types';
import { calculateCurrentStep } from '../../utils';
import { isProductsLoadingCompleteAtom } from '../../../../features/templateSelection/atoms';

export function LoadingGrid({ products, totalSteps = 5 }: LoadingGridProps) {
  const setProductsLoadingComplete = useSetAtom(isProductsLoadingCompleteAtom);
  const [, setActiveImages] = useAtom(activeImagesAtom);
  const [, setEnabledImages] = useAtom(enabledImagesAtom);
  const [progress, setProgress] = useAtom(progressAtom);

  const currentStep = calculateCurrentStep(progress, totalSteps);
  const { getStepText } = useLoadingSteps({ currentStep });

  const { displayProducts } = useImageLoader({
    products,
    maxDisplayProducts: 6,
  });

  const PRODUCT_DELAY = TOTAL_ANIMATION_TIME / (displayProducts.length + 1);

  useEffect(() => {
    let animationFrame: number;
    const startTime = Date.now();

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;

      if (elapsed >= TOTAL_ANIMATION_TIME) {
        setProgress(100);
        const allProducts = new Set(Array.from({ length: displayProducts.length }, (_, i) => i));
        setActiveImages(allProducts);
        setEnabledImages(allProducts);
        setProductsLoadingComplete(true);
        return;
      }

      // Calculate progress
      const newProgress = (elapsed / TOTAL_ANIMATION_TIME) * 100;
      setProgress(newProgress);

      // Calculate which products should be active (showing checkmark)
      const productsToShow = Math.min(Math.floor(elapsed / PRODUCT_DELAY + 0.5), displayProducts.length);
      setActiveImages(new Set(Array.from({ length: productsToShow }, (_, i) => i)));

      // Calculate which products should be enabled (overlay removed)
      const productsToEnable = Math.min(
        Math.floor((elapsed - ENABLE_DELAY) / PRODUCT_DELAY + 0.5),
        displayProducts.length,
      );
      setEnabledImages(new Set(Array.from({ length: Math.max(0, productsToEnable) }, (_, i) => i)));

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  return (
    <Box className={s.container} direction="vertical" gap={4} width="100%">
      <ProgressSection progress={progress} currentStep={currentStep} getStepText={getStepText} />

      <FooterSection progress={progress} productsCount={products.length} />
    </Box>
  );
}
